import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Ticket, Star, Clock, CheckCircle, Search, TrendingUp, ChevronDown, ChevronUp, PlayCircle, X, Users, Trash2, HelpCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useTour } from '../hooks/useTour';
import GuidedTour from '../components/GuidedTour';
import axios from 'axios';
import { isAuthenticated, getAuthHeader, getCertifiedNetworkStatus } from '../services/otpAuthService';

interface Voucher {
  _id: string;
  tariffario: string;
  pratica: {
    categoria: string;
    catalogo: string;
    descrizionePratica: string;
    codicePratica: string;
  };
  causale: string;
  quantita: number;
  isCumulativo?: boolean;
  numeroPratiche?: number;
  stato: 'disponibile' | 'prenotato' | 'completato' | 'annullato' | 'scaduto';
  proprietario: {
    _id: string;
    ragioneSociale: string;
    codice: string;
  };
  prenotante?: {
    _id: string;
    ragioneSociale: string;
    codice: string;
  };
  dataPrenotazione?: string;
  scadenzaPrenotazione?: string;
  createdAt: string;
}

interface Stats {
  mieiVoucher: number;
  voucherPrenotati: number;
  prenotazioniRicevute: number;
  messaggiNonLetti: number;
  voucherDisponibili: number;
}

interface ConfirmPrenotazioneData {
  vouchers: Array<{ _id: string; quantita: number; createdAt: string }>;
  quantita: number;
  descrizionePratica: string;
  codicePratica: string;
  tariffario: string;
  proprietarioNome: string;
  tariffe?: Tariffe;
  isCumulativo?: boolean;
  numeroPratiche?: number;
}

interface Tariffe {
  diritti?: number;
  bolli1?: number;
  bolli2?: number;
  costo?: number;
  diritti2?: number;
  diritti3?: number;
  diritti4?: number;
  gas?: number;
  contributi?: number;
  dirittiSted?: number;
  corrispettivoSted?: number;
  tributiSpeciali?: number;
  rimborsoMissioni?: number;
  esenzione?: boolean;
  cumulabile?: boolean;
}

interface Pratica {
  categoria: string;
  catalogo: string;
  descrizionePratica: string;
  tariffe?: Tariffe;
}

interface Interesse {
  _id: string;
  tariffario: string;
  codiceTariffa: string;
  attivo: boolean;
  notificheEmail: boolean;
  pratiche?: Pratica[];
  createdAt: string;
}

interface VoucherGruppoDescrizione {
  descrizionePratica: string;
  categoria: string;
  catalogo: string;
  vouchers: Array<{
    _id: string;
    quantita: number;
    dataScadenza: string;
    createdAt: string;
    isCumulativo?: boolean;
    numeroPratiche?: number;
  }>;
}

interface VoucherGruppoProprietario {
  proprietario: {
    _id: string;
    nominativo: string;
    rappresentante: string;
    codice: string;
  };
  totaleQuantita: number;
  primoVoucherData: string;
  descrizioni: VoucherGruppoDescrizione[];
}

interface VoucherRaggruppato {
  codicePratica: string;
  tariffario: string;
  tariffe: Tariffe;
  isCumulativo?: boolean;
  numeroPratiche?: number;
  totaleQuantita: number;
  proprietari: VoucherGruppoProprietario[];
}

const RimborsoVoucherPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'disponibili' | 'miei' | 'prenotati' | 'ricevuti' | 'interessi'>('disponibili');
  const [, setVouchersDisponibili] = useState<Voucher[]>([]);
  const [vouchersRaggruppati, setVouchersRaggruppati] = useState<VoucherRaggruppato[]>([]);
  const [sezioniAperte, setSezioniAperte] = useState<Set<string>>(new Set());
  const [mieiVouchers, setMieiVouchers] = useState<Voucher[]>([]);
  const [vouchersPrenotati, setVouchersPrenotati] = useState<Voucher[]>([]);
  const [vouchersRicevuti, setVouchersRicevuti] = useState<Voucher[]>([]);
  const [interessi, setInteressi] = useState<Interesse[]>([]);
  const [stats, setStats] = useState<Stats>({ mieiVoucher: 0, voucherPrenotati: 0, prenotazioniRicevute: 0, messaggiNonLetti: 0, voucherDisponibili: 0 });
  const [filtroTariffario, setFiltroTariffario] = useState<string>('tutti');
  const [searchQuery, setSearchQuery] = useState('');
  const [quantitaDaPrenotare, setQuantitaDaPrenotare] = useState<{ [key: string]: number }>({});
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<'rimborso' | 'creazione'>('rimborso');
  const [confirmPrenotazione, setConfirmPrenotazione] = useState<ConfirmPrenotazioneData | null>(null);

  // Guided tour
  const { runTour, tourKey, startTour, handleCallback } = useTour('voucher_dashboard_tour');

  const tourSteps = [
    {
      target: '#tour-voucher-nuovo',
      content: 'Clicca qui per pubblicare un nuovo voucher disponibile per gli altri studi.',
      skipBeacon: true,
      placement: 'bottom' as const
    },
    {
      target: '#tour-voucher-stats',
      content: 'Qui trovi un riepilogo rapido dei tuoi voucher, prenotazioni e messaggi.',
      placement: 'bottom' as const
    },
    {
      target: '#tour-voucher-tabs',
      content: 'Naviga tra le diverse sezioni: voucher disponibili, i tuoi, quelli prenotati, ricevuti e i tuoi interessi.',
      placement: 'bottom' as const
    },
    {
      target: '#tour-voucher-filtri',
      content: 'Usa i filtri per cercare voucher per tariffario o codice pratica.',
      placement: 'bottom' as const
    }
  ];

  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  useEffect(() => {
    const checkAccessAndLoadData = async () => {
      // Verifica autenticazione
      if (!isAuthenticated()) {
        toast.error('Devi effettuare l\'accesso');
        navigate('/accesso-associati');
        return;
      }

      // Verifica stato membro certificato
      try {
        const status = await getCertifiedNetworkStatus();

        if (!status.isMember) {
          toast.error('Accesso riservato ai membri della Rete Certificata UNASCA', {
            duration: 5000,
            icon: '🔒',
          });
          navigate('/area-riservata');
          return;
        }

        if (!status.isActive) {
          toast.error('Il tuo account non è ancora stato attivato. Attendi l\'attivazione da parte della Segreteria Nazionale UNASCA.', {
            duration: 6000,
            icon: '⏳',
          });
          navigate('/area-riservata');
          return;
        }

        // Se tutto ok, carica i dati
        loadData();
      } catch (error) {
        console.error('Errore verifica stato membro:', error);
        toast.error('Errore durante la verifica dello stato membro');
        navigate('/area-riservata');
      }
    };

    checkAccessAndLoadData();

    // Auto-refresh ogni 30 secondi
    const interval = setInterval(() => {
      loadData(true); // true = auto-refresh silenzioso
    }, 30000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [navigate]);

  const loadData = async (isAutoRefresh = false) => {
    try {
      if (isAutoRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Carica statistiche
      const statsResponse = await axios.get(
        `${API_BASE_URL}/api/voucher-rimborso/stats/dashboard`,
        { headers: getAuthHeader() }
      );
      setStats(statsResponse.data.data);

      // Carica voucher disponibili (vecchia API per compatibilità)
      const disponibiliResponse = await axios.get(
        `${API_BASE_URL}/api/voucher-rimborso?stato=disponibile&limit=50`,
        { headers: getAuthHeader() }
      );
      setVouchersDisponibili(disponibiliResponse.data.data);

      // Carica voucher disponibili raggruppati (nuova API)
      const raggrupatiResponse = await axios.get(
        `${API_BASE_URL}/api/voucher-rimborso/disponibili-raggruppati`,
        { headers: getAuthHeader() }
      );
      setVouchersRaggruppati(raggrupatiResponse.data.data);

      // Carica i miei voucher
      const mieiResponse = await axios.get(
        `${API_BASE_URL}/api/voucher-rimborso?proprietario=me&limit=50`,
        { headers: getAuthHeader() }
      );
      setMieiVouchers(mieiResponse.data.data);

      // Carica voucher prenotati (inclusi completati)
      const prenotatiResponse = await axios.get(
        `${API_BASE_URL}/api/voucher-rimborso?prenotante=me&limit=50`,
        { headers: getAuthHeader() }
      );
      // Filtra solo prenotati e completati
      const prenotatiECompletati = prenotatiResponse.data.data.filter(
        (v: Voucher) => v.stato === 'prenotato' || v.stato === 'completato'
      );
      setVouchersPrenotati(prenotatiECompletati);

      // Carica prenotazioni ricevute (voucher miei prenotati da altri)
      const ricevutiResponse = await axios.get(
        `${API_BASE_URL}/api/voucher-rimborso?proprietario=me&stato=prenotato&limit=50`,
        { headers: getAuthHeader() }
      );
      setVouchersRicevuti(ricevutiResponse.data.data);

      // Carica interessi
      const interessiResponse = await axios.get(
        `${API_BASE_URL}/api/voucher-interessi`,
        { headers: getAuthHeader() }
      );
      setInteressi(interessiResponse.data.data);

    } catch (error: any) {
      console.error('Errore caricamento dati:', error);
      // Mostra errore solo se non è auto-refresh
      if (!isAutoRefresh) {
        toast.error(error.response?.data?.message || 'Errore durante il caricamento');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Funzione per formattare gli importi
  const getImportiPrincipali = (tariffe?: Tariffe) => {
    if (!tariffe) return null;

    const importi: string[] = [];

    const formatImporto = (valore: any) => {
      const num = typeof valore === 'string' ? parseFloat(valore) : valore;
      return !isNaN(num) && num > 0 ? num.toFixed(2) : null;
    };

    // Somma tutti i diritti
    let totDiritti = 0;
    const diritti = formatImporto(tariffe.diritti);
    const diritti2 = formatImporto(tariffe.diritti2);
    const diritti3 = formatImporto(tariffe.diritti3);
    const diritti4 = formatImporto(tariffe.diritti4);
    if (diritti) totDiritti += parseFloat(diritti);
    if (diritti2) totDiritti += parseFloat(diritti2);
    if (diritti3) totDiritti += parseFloat(diritti3);
    if (diritti4) totDiritti += parseFloat(diritti4);

    // Somma tutti i bolli
    let totBolli = 0;
    const bolli1 = formatImporto(tariffe.bolli1);
    const bolli2 = formatImporto(tariffe.bolli2);
    if (bolli1) totBolli += parseFloat(bolli1);
    if (bolli2) totBolli += parseFloat(bolli2);

    const contributi = formatImporto(tariffe.contributi);
    const costo = formatImporto(tariffe.costo);
    const gas = formatImporto(tariffe.gas);
    const dirittiSted = formatImporto(tariffe.dirittiSted);
    const corrispettivoSted = formatImporto(tariffe.corrispettivoSted);
    const tributiSpeciali = formatImporto(tariffe.tributiSpeciali);
    const rimborsoMissioni = formatImporto(tariffe.rimborsoMissioni);

    if (totDiritti > 0) importi.push(`Diritti: €${totDiritti.toFixed(2)}`);
    if (totBolli > 0) importi.push(`Bolli: €${totBolli.toFixed(2)}`);
    if (contributi) importi.push(`Contributi: €${contributi}`);
    if (costo) importi.push(`Costo: €${costo}`);
    if (gas) importi.push(`Gas: €${gas}`);
    if (dirittiSted) importi.push(`Diritti STED: €${dirittiSted}`);
    if (corrispettivoSted) importi.push(`Corrispettivo STED: €${corrispettivoSted}`);
    if (tributiSpeciali) importi.push(`Tributi Speciali: €${tributiSpeciali}`);
    if (rimborsoMissioni) importi.push(`Rimborso Missioni: €${rimborsoMissioni}`);

    return importi.length > 0 ? importi.join(' • ') : null;
  };

  const getTariffarioLabel = (tariffario: string) => {
    const map: { [key: string]: string } = {
      'NAZIONALE': 'Nazionale',
      'TRENTO': 'Trento',
      'BOLZANO': 'Bolzano',
      'SICILIA': 'Sicilia',
      'AOSTA': 'Valle d\'Aosta',
      'FRIULI_VENEZIA_GIULIA': 'Friuli V.G.'
    };
    return map[tariffario] || tariffario;
  };

  // Colore distintivo per ogni tariffario
  const getTariffarioConfig = (tariffario: string) => {
    const configs: { [key: string]: { badge: string; border: string; dot: string } } = {
      'NAZIONALE': {
        badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 ring-1 ring-blue-300 dark:ring-blue-700',
        border: 'border-l-4 border-l-blue-500',
        dot: 'bg-blue-500'
      },
      'BOLZANO': {
        badge: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 ring-1 ring-red-300 dark:ring-red-700',
        border: 'border-l-4 border-l-red-500',
        dot: 'bg-red-500'
      },
      'TRENTO': {
        badge: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-300 dark:ring-emerald-700',
        border: 'border-l-4 border-l-emerald-500',
        dot: 'bg-emerald-500'
      },
      'SICILIA': {
        badge: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 ring-1 ring-amber-300 dark:ring-amber-700',
        border: 'border-l-4 border-l-amber-500',
        dot: 'bg-amber-500'
      },
      'AOSTA': {
        badge: 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 ring-1 ring-violet-300 dark:ring-violet-700',
        border: 'border-l-4 border-l-violet-500',
        dot: 'bg-violet-500'
      },
      'FRIULI_VENEZIA_GIULIA': {
        badge: 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 ring-1 ring-teal-300 dark:ring-teal-700',
        border: 'border-l-4 border-l-teal-500',
        dot: 'bg-teal-500'
      },
    };
    return configs[tariffario] || {
      badge: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 ring-1 ring-gray-300 dark:ring-gray-600',
      border: 'border-l-4 border-l-gray-400',
      dot: 'bg-gray-400'
    };
  };

  const getStatoColor = (stato: string) => {
    const colors: { [key: string]: string } = {
      'disponibile': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      'prenotato': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      'completato': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      'annullato': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      'scaduto': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
    };
    return colors[stato] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  };

  // Toggle apertura sezione accordion
  const toggleSezione = (chiave: string) => {
    setSezioniAperte(prev => {
      const nuove = new Set(prev);
      if (nuove.has(chiave)) {
        nuove.delete(chiave);
      } else {
        nuove.add(chiave);
      }
      return nuove;
    });
  };

  // Prenota voucher con quantità specifica
  // Vai al messaggio non letto più vecchio
  const vaiAlMessaggioNonLetto = async () => {
    try {
      // Carica tutti i voucher con messaggi non letti
      const response = await axios.get(
        `${API_BASE_URL}/api/voucher-rimborso?messaggiNonLetti=true`,
        { headers: getAuthHeader() }
      );

      const vouchersConMessaggi = response.data.data;

      if (vouchersConMessaggi.length === 0) {
        toast.info('Nessun messaggio da leggere');
        return;
      }

      // Trova il voucher con il messaggio più vecchio
      // Ordina per data del messaggio più vecchio non letto
      const voucherConMessaggioPiuVecchio = vouchersConMessaggi[0];

      // Naviga al voucher
      navigate(`/area-riservata/rimborso-voucher/${voucherConMessaggioPiuVecchio._id}`);
    } catch (error) {
      console.error('Errore caricamento messaggi:', error);
      toast.error('Errore durante il caricamento dei messaggi');
    }
  };

  const prenotaVoucherConQuantita = async (
    vouchers: Array<{ _id: string; quantita: number; createdAt: string }>,
    quantitaDaPrenotare: number
  ) => {
    try {
      // Ordina i voucher per data (più vecchio prima)
      const vouchersOrdinati = [...vouchers].sort((a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

      let quantitaRimanente = quantitaDaPrenotare;
      const vouchersPrenotati: string[] = [];

      // Prenota i voucher necessari partendo dal più vecchio
      for (const voucher of vouchersOrdinati) {
        if (quantitaRimanente <= 0) break;

        if (voucher.quantita <= quantitaRimanente) {
          // Prenota tutto il voucher
          await axios.post(
            `${API_BASE_URL}/api/voucher-rimborso/${voucher._id}/prenota`,
            {},
            { headers: getAuthHeader() }
          );
          vouchersPrenotati.push(voucher._id);
          quantitaRimanente -= voucher.quantita;
        } else {
          // Il voucher ha più quantità di quella richiesta
          // Prenota solo la quantità richiesta (split del voucher)
          const response = await axios.post(
            `${API_BASE_URL}/api/voucher-rimborso/${voucher._id}/prenota`,
            { quantita: quantitaRimanente },
            { headers: getAuthHeader() }
          );
          vouchersPrenotati.push(response.data.data._id);
          quantitaRimanente = 0;
        }
      }

      if (quantitaRimanente > 0) {
        toast.error(`Quantità richiesta non disponibile. Disponibili solo ${quantitaDaPrenotare - quantitaRimanente} voucher.`);
        return;
      }

      toast.success(`${quantitaDaPrenotare} voucher prenotati con successo!`);

      // Redirect al primo voucher prenotato
      if (vouchersPrenotati.length > 0) {
        navigate(`/area-riservata/rimborso-voucher/${vouchersPrenotati[0]}`);
      } else {
        loadData();
      }
    } catch (error: any) {
      console.error('Errore prenotazione:', error);
      toast.error(error.response?.data?.message || 'Errore durante la prenotazione');
    }
  };

  // Calcola totale importi da tariffe
  const getTotaleImporti = (tariffe?: Tariffe): number => {
    if (!tariffe) return 0;
    let totale = 0;
    const addVal = (v: any) => { const n = typeof v === 'string' ? parseFloat(v) : v; if (n && !isNaN(n) && n > 0) totale += n; };
    addVal(tariffe.diritti); addVal(tariffe.diritti2); addVal(tariffe.diritti3); addVal(tariffe.diritti4);
    addVal(tariffe.bolli1); addVal(tariffe.bolli2);
    addVal(tariffe.contributi); addVal(tariffe.costo); addVal(tariffe.gas);
    addVal(tariffe.dirittiSted); addVal(tariffe.corrispettivoSted); addVal(tariffe.tributiSpeciali); addVal(tariffe.rimborsoMissioni);
    return totale;
  };

  // Intercetta click PRENOTA e mostra modal di conferma
  const handlePrenotaClick = (
    vouchers: Array<{ _id: string; quantita: number; createdAt: string }>,
    quantita: number,
    descrizionePratica: string,
    codicePratica: string,
    tariffario: string,
    proprietarioNome: string,
    tariffe?: Tariffe,
    isCumulativo?: boolean,
    numeroPratiche?: number
  ) => {
    setConfirmPrenotazione({ vouchers, quantita, descrizionePratica, codicePratica, tariffario, proprietarioNome, tariffe, isCumulativo, numeroPratiche });
  };

  // Elimina un voucher proprio disponibile
  const handleDeleteVoucher = async (voucherId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm('Sei sicuro di voler eliminare questo voucher? L\'operazione non è reversibile.')) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/voucher-rimborso/${voucherId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Voucher eliminato con successo');
      loadData();
    } catch (error: any) {
      console.error('Errore eliminazione:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'eliminazione');
    }
  };

  const getStatoLabel = (stato: string) => {
    const labels: { [key: string]: string } = {
      'disponibile': 'Disponibile',
      'prenotato': 'Prenotato',
      'completato': 'Completato',
      'annullato': 'Annullato',
      'scaduto': 'Scaduto'
    };
    return labels[stato] || stato;
  };

  const filterVouchers = (vouchers: Voucher[]) => {
    return vouchers.filter(v => {
      const matchTariffario = filtroTariffario === 'tutti' || v.tariffario === filtroTariffario;
      const matchSearch = !searchQuery ||
        v.pratica.descrizionePratica.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.pratica.codicePratica.toLowerCase().includes(searchQuery.toLowerCase());
      return matchTariffario && matchSearch;
    });
  };

  const filterRaggruppamenti = (raggruppamenti: VoucherRaggruppato[]) => {
    return raggruppamenti.filter(gruppo => {
      const matchTariffario = filtroTariffario === 'tutti' || gruppo.tariffario === filtroTariffario;
      const matchSearch = !searchQuery ||
        gruppo.codicePratica.toLowerCase().includes(searchQuery.toLowerCase()) ||
        gruppo.proprietari.some(prop =>
          prop.descrizioni.some(d => d.descrizionePratica.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      return matchTariffario && matchSearch;
    });
  };

  const renderVoucherCard = (voucher: Voucher, showProprietario: boolean = true, canDelete: boolean = false) => (
    <div
      key={voucher._id}
      onClick={() => navigate(`/area-riservata/rimborso-voucher/${voucher._id}`)}
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-shadow cursor-pointer"
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
              {voucher.pratica.codicePratica}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {getTariffarioLabel(voucher.tariffario)}
            </span>
            {voucher.isCumulativo && (
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded">
                ⚡ {voucher.numeroPratiche} cumulative
              </span>
            )}
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
            {voucher.pratica.descrizionePratica}
          </h3>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {voucher.pratica.categoria} • {voucher.causale}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canDelete && voucher.stato === 'disponibile' && (
            <button
              onClick={(e) => handleDeleteVoucher(voucher._id, e)}
              className="p-1.5 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title="Elimina voucher"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <span className={`text-xs font-medium px-2 py-1 rounded ${getStatoColor(voucher.stato)}`}>
            {getStatoLabel(voucher.stato)}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        {showProprietario && (
          <span>
            <Ticket className="w-3 h-3 inline mr-1" />
            {voucher.proprietario.ragioneSociale}
          </span>
        )}
        <span>Qtà: {voucher.quantita}</span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <GuidedTour steps={tourSteps} run={runTour} tourKey={tourKey} onCallback={handleCallback} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/area-riservata')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Torna alla Dashboard
          </button>

          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                Rimborso Voucher PagoPA
                {refreshing && (
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Aggiornamento...
                  </span>
                )}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Scambia voucher con altri associati della rete certificata
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={startTour}
                className="p-2 rounded-lg text-gray-400 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors"
                title="Guida"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowVideoModal(true)}
                className="flex items-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                title="Guarda il video tutorial"
              >
                <PlayCircle className="w-5 h-5" />
                <span className="hidden sm:inline">Video Tutorial</span>
              </button>
              <button
                id="tour-voucher-nuovo"
                onClick={() => navigate('/area-riservata/rimborso-voucher/nuovo')}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                <Plus className="w-5 h-5" />
                Nuovo Voucher
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div id="tour-voucher-stats" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          {/* Disponibili */}
          <div
            onClick={() => setActiveTab('disponibili')}
            className={`group relative bg-white dark:bg-gray-800 rounded-xl border-2 p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${activeTab === 'disponibili' ? 'border-green-500 shadow-md shadow-green-100 dark:shadow-green-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-green-400'}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.voucherDisponibili}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-medium">Disponibili</p>
          </div>

          {/* I Miei Voucher */}
          <div
            onClick={() => setActiveTab('miei')}
            className={`group relative bg-white dark:bg-gray-800 rounded-xl border-2 p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${activeTab === 'miei' ? 'border-purple-500 shadow-md shadow-purple-100 dark:shadow-purple-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-purple-400'}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.mieiVoucher}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-medium">I Miei Voucher</p>
          </div>

          {/* Prenotati da Me */}
          <div
            onClick={() => setActiveTab('prenotati')}
            className={`group relative bg-white dark:bg-gray-800 rounded-xl border-2 p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${activeTab === 'prenotati' ? 'border-blue-500 shadow-md shadow-blue-100 dark:shadow-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-blue-400'}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.voucherPrenotati}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-medium">Prenotati da Me</p>
          </div>

          {/* Prenotazioni Ricevute */}
          <div
            onClick={() => setActiveTab('ricevuti')}
            className={`group relative bg-white dark:bg-gray-800 rounded-xl border-2 p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${activeTab === 'ricevuti' ? 'border-amber-500 shadow-md shadow-amber-100 dark:shadow-amber-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-amber-400'}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              {stats.prenotazioniRicevute > 0 && (
                <span className="flex items-center justify-center w-5 h-5 bg-amber-500 text-white text-xs rounded-full font-bold animate-pulse">
                  {stats.prenotazioniRicevute}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.prenotazioniRicevute}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-medium">Prenotaz. Ricevute</p>
          </div>

          {/* Messaggi */}
          <div
            onClick={stats.messaggiNonLetti > 0 ? vaiAlMessaggioNonLetto : undefined}
            className={`group relative bg-white dark:bg-gray-800 rounded-xl border-2 p-4 transition-all duration-200 ${
              stats.messaggiNonLetti > 0
                ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 border-orange-500 shadow-md shadow-orange-100 dark:shadow-orange-900/20'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              {stats.messaggiNonLetti > 0 && (
                <span className="flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs rounded-full font-bold animate-pulse">
                  {stats.messaggiNonLetti}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.messaggiNonLetti}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-medium">Messaggi</p>
          </div>
        </div>

        {/* Tabs */}
        <div id="tour-voucher-tabs" className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-hide">
            <nav className="flex -mb-px min-w-max">
              {[
                { id: 'disponibili', label: 'Disponibili', icon: Ticket },
                { id: 'miei', label: 'I Miei', icon: TrendingUp },
                { id: 'prenotati', label: 'Prenotati da Me', icon: Clock },
                { id: 'ricevuti', label: 'Ricevuti', icon: Users, badge: stats.prenotazioniRicevute },
                { id: 'interessi', label: 'Interessi ⭐', icon: Star }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`relative flex items-center gap-2 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.id === 'disponibili' ? 'Disp.' : tab.id === 'prenotati' ? 'Pren.' : tab.id === 'ricevuti' ? 'Ric.' : tab.id === 'interessi' ? '⭐' : tab.label}</span>
                  {'badge' in tab && (tab as any).badge > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center w-5 h-5 bg-amber-500 text-white text-xs rounded-full font-bold">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Filtri */}
          {activeTab !== 'interessi' && (
            <div id="tour-voucher-filtri" className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Cerca per descrizione o codice tariffa..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                <div className="sm:w-64">
                  <select
                    value={filtroTariffario}
                    onChange={(e) => setFiltroTariffario(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="tutti">Tutti i Tariffari</option>
                    <option value="NAZIONALE">Nazionale</option>
                    <option value="TRENTO">Trento</option>
                    <option value="BOLZANO">Bolzano</option>
                    <option value="SICILIA">Sicilia</option>
                    <option value="AOSTA">Valle d'Aosta</option>
                    <option value="FRIULI_VENEZIA_GIULIA">Friuli V.G.</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            {activeTab === 'disponibili' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Voucher Disponibili ({filterRaggruppamenti(vouchersRaggruppati).reduce((sum, g) => sum + g.totaleQuantita, 0)} unità)
                </h2>
                {filterRaggruppamenti(vouchersRaggruppati).length === 0 ? (
                  <div className="text-center py-12">
                    <Ticket className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">Nessun voucher disponibile al momento</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filterRaggruppamenti(vouchersRaggruppati).map(gruppo => {
                      const chiave = `${gruppo.codicePratica}|${gruppo.tariffario}`;
                      const isAperta = sezioniAperte.has(chiave);

                      const tariffarioConfig = getTariffarioConfig(gruppo.tariffario);
                      return (
                        <div key={chiave} className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden ${tariffarioConfig.border}`}>
                          {/* Header sezione - cliccabile */}
                          <button
                            onClick={() => toggleSezione(chiave)}
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="text-sm font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded">
                                {gruppo.codicePratica}
                              </span>
                              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${tariffarioConfig.badge}`}>
                                {getTariffarioLabel(gruppo.tariffario)}
                              </span>
                              {gruppo.isCumulativo && (
                                <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded-full">
                                  ⚡ Cumulativo {gruppo.numeroPratiche ? `(${gruppo.numeroPratiche} pratiche)` : ''}
                                </span>
                              )}
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {getImportiPrincipali(gruppo.tariffe)}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({gruppo.totaleQuantita} {gruppo.totaleQuantita === 1 ? 'voucher' : 'voucher'})
                              </span>
                            </div>
                            {isAperta ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </button>

                          {/* Contenuto espandibile - Proprietari */}
                          {isAperta && (
                            <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-3">
                              {gruppo.proprietari.map((prop, propIdx) => (
                                <div key={propIdx} className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 space-y-2">
                                  {/* Info proprietario */}
                                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-2">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                          {prop.proprietario.nominativo}
                                        </p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                          Codice: {prop.proprietario.codice}
                                        </p>
                                      </div>
                                      <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                        {prop.totaleQuantita} voucher
                                      </span>
                                    </div>
                                  </div>

                                  {/* Descrizioni del proprietario */}
                                  {prop.descrizioni.map((desc, descIdx) => {
                                    const totaleQuantitaDesc = desc.vouchers.reduce((sum: number, v) => sum + v.quantita, 0);
                                    const chiaveDesc = `${gruppo.codicePratica}|${gruppo.tariffario}|${prop.proprietario._id}|${descIdx}`;
                                    const quantitaSelezionata = quantitaDaPrenotare[chiaveDesc] || 1;

                                    return (
                                      <div key={descIdx} className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                        {/* Header con descrizione e pulsante prenota */}
                                        <div className="flex items-center justify-between gap-3 mb-2">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                {desc.descrizionePratica}
                                              </h4>
                                              {desc.vouchers.some(v => v.isCumulativo) && (
                                                <span className="text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
                                                  ⚡ {desc.vouchers.find(v => v.isCumulativo)?.numeroPratiche} cum.
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                              {desc.categoria} • {desc.catalogo}
                                            </p>
                                          </div>

                                          <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                                              {totaleQuantitaDesc} disponibili
                                            </span>

                                            {/* Input quantità */}
                                            <div className="flex items-center gap-1">
                                              <label className="text-xs text-gray-600 dark:text-gray-400">Qtà:</label>
                                              <input
                                                type="number"
                                                min="1"
                                                max={totaleQuantitaDesc}
                                                value={quantitaSelezionata}
                                                onChange={(e) => {
                                                  const val = parseInt(e.target.value) || 1;
                                                  setQuantitaDaPrenotare(prev => ({
                                                    ...prev,
                                                    [chiaveDesc]: Math.min(Math.max(1, val), totaleQuantitaDesc)
                                                  }));
                                                }}
                                                className="w-16 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                              />
                                            </div>

                                            {/* Pulsante prenota */}
                                            <button
                                              onClick={() => handlePrenotaClick(
                                                desc.vouchers,
                                                quantitaSelezionata,
                                                desc.descrizionePratica,
                                                gruppo.codicePratica,
                                                gruppo.tariffario,
                                                prop.proprietario.nominativo,
                                                gruppo.tariffe,
                                                gruppo.isCumulativo,
                                                gruppo.numeroPratiche
                                              )}
                                              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-1.5 rounded-full font-semibold transition-colors text-xs whitespace-nowrap"
                                            >
                                              Prenota
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'miei' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  I Miei Voucher ({filterVouchers(mieiVouchers).length})
                </h2>
                {filterVouchers(mieiVouchers).length === 0 ? (
                  <div className="text-center py-12">
                    <TrendingUp className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 mb-4">Non hai ancora caricato voucher</p>
                    <button
                      onClick={() => navigate('/area-riservata/rimborso-voucher/nuovo')}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                    >
                      Carica il Primo Voucher
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filterVouchers(mieiVouchers).map(v => renderVoucherCard(v, false, true))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'prenotati' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Voucher Prenotati da Me ({filterVouchers(vouchersPrenotati).length})
                </h2>
                {filterVouchers(vouchersPrenotati).length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">Non hai prenotato nessun voucher</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filterVouchers(vouchersPrenotati).map(v => renderVoucherCard(v, true))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'ricevuti' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Prenotazioni Ricevute ({filterVouchers(vouchersRicevuti).length})
                  </h2>
                </div>
                {filterVouchers(vouchersRicevuti).length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">Nessun tuo voucher è stato prenotato da altri associati</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filterVouchers(vouchersRicevuti).map(v => {
                      const tariffarioConfig = getTariffarioConfig(v.tariffario);
                      return (
                        <div
                          key={v._id}
                          onClick={() => navigate(`/area-riservata/rimborso-voucher/${v._id}`)}
                          className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 cursor-pointer hover:shadow-md transition-all ${tariffarioConfig.border}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-xs font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded">
                                  {v.pratica.codicePratica}
                                </span>
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tariffarioConfig.badge}`}>
                                  {getTariffarioLabel(v.tariffario)}
                                </span>
                                <span className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400 px-2 py-0.5 rounded font-medium">
                                  Prenotato
                                </span>
                              </div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                {v.pratica.descrizionePratica}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Prenotante: <span className="font-medium text-gray-700 dark:text-gray-300">{v.prenotante?.ragioneSociale || '—'}</span>
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{v.quantita}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">voucher</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'interessi' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    I Miei Interessi ({interessi.length})
                  </h2>
                  <button
                    onClick={() => navigate('/area-riservata/rimborso-voucher/interessi')}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm"
                  >
                    Gestisci Interessi
                  </button>
                </div>

                {interessi.length === 0 ? (
                  <div className="text-center py-12">
                    <Star className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      Nessun interesse configurato
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Specifica le tariffe di tuo interesse e ricevi notifiche quando disponibili
                    </p>
                    <button
                      onClick={() => navigate('/area-riservata/rimborso-voucher/interessi')}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                    >
                      Aggiungi il Primo Interesse
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {interessi.map((interesse) => (
                      <div
                        key={interesse._id}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded">
                              {interesse.codiceTariffa}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {getTariffarioLabel(interesse.tariffario)}
                            </span>
                            {interesse.attivo ? (
                              <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 px-2 py-1 rounded">
                                Attivo
                              </span>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400 px-2 py-1 rounded">
                                Disattivato
                              </span>
                            )}
                          </div>
                        </div>

                        {interesse.pratiche && interesse.pratiche.length > 0 && (
                          <div className="space-y-2 pl-4 border-l-2 border-purple-200 dark:border-purple-800">
                            {interesse.pratiche.map((pratica, idx) => (
                              <div key={idx} className="text-sm">
                                <p className="text-gray-900 dark:text-gray-100 font-medium">
                                  {pratica.descrizionePratica}
                                </p>
                                <p className="text-gray-500 dark:text-gray-400 text-xs">
                                  {pratica.categoria} • {pratica.catalogo}
                                </p>
                                {getImportiPrincipali(pratica.tariffe) && (
                                  <p className="text-green-600 dark:text-green-400 text-xs font-medium mt-1">
                                    💰 {getImportiPrincipali(pratica.tariffe)}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
            ℹ️ Come funziona
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <li>1. Carica i voucher PagoPA che non utilizzi</li>
            <li>2. Cerca voucher disponibili che ti interessano</li>
            <li>3. Prenota un voucher e contatta il proprietario tramite messaggistica</li>
            <li>4. Il proprietario carica il PDF del voucher; il prenotante paga (es. bonifico) e carica la ricevuta</li>
            <li>5. Ognuno conferma solo dopo aver ricevuto la propria parte: il prenotante il voucher, il proprietario il rimborso</li>
            <li>6. ⭐ Imposta le tue preferenze per ricevere notifiche automatiche</li>
          </ul>
        </div>
      </div>

      {/* Modale Conferma Prenotazione */}
      {confirmPrenotazione && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setConfirmPrenotazione(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">Conferma Prenotazione</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Verifica i dettagli prima di procedere</p>
                </div>
              </div>
              <button
                onClick={() => setConfirmPrenotazione(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Dettagli */}
            <div className="px-6 py-5 space-y-4">
              {/* Tariffario badge */}
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${getTariffarioConfig(confirmPrenotazione.tariffario).badge}`}>
                  {getTariffarioLabel(confirmPrenotazione.tariffario)}
                </span>
                <span className="text-xs font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded-full">
                  {confirmPrenotazione.codicePratica}
                </span>
              </div>

              {/* Pratica */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide font-medium">Pratica</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{confirmPrenotazione.descrizionePratica}</p>
              </div>

              {/* Proprietario */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                <p className="text-xs text-blue-600 dark:text-blue-400 mb-1 uppercase tracking-wide font-medium">Proprietario Voucher</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{confirmPrenotazione.proprietarioNome}</p>
              </div>

              {/* Quantità */}
              <div className="flex items-center justify-between bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                <div>
                  <p className="text-xs text-purple-600 dark:text-purple-400 uppercase tracking-wide font-medium mb-1">Quantità da Prenotare</p>
                  <p className="text-3xl font-black text-purple-700 dark:text-purple-300">{confirmPrenotazione.quantita}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 dark:text-gray-400">voucher disponibili</p>
                  <p className="text-lg font-bold text-gray-700 dark:text-gray-300">
                    {confirmPrenotazione.vouchers.reduce((s, v) => s + v.quantita, 0)}
                  </p>
                </div>
              </div>

              {/* Dettagli Pagamento */}
              {confirmPrenotazione.tariffe && getTotaleImporti(confirmPrenotazione.tariffe) > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
                  <p className="text-xs text-green-600 dark:text-green-400 uppercase tracking-wide font-medium mb-2">Dettagli Pagamento</p>
                  {getImportiPrincipali(confirmPrenotazione.tariffe) && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      {getImportiPrincipali(confirmPrenotazione.tariffe)}
                    </p>
                  )}
                  {confirmPrenotazione.isCumulativo && confirmPrenotazione.numeroPratiche && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                      ⚡ Voucher cumulativo: {confirmPrenotazione.numeroPratiche} pratiche
                    </p>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-green-200 dark:border-green-800">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Totale da pagare:</p>
                    <p className="text-xl font-black text-green-700 dark:text-green-300">
                      €{(getTotaleImporti(confirmPrenotazione.tariffe) * confirmPrenotazione.quantita).toFixed(2)}
                    </p>
                  </div>
                  {confirmPrenotazione.quantita > 1 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-right mt-1">
                      ({confirmPrenotazione.quantita} × €{getTotaleImporti(confirmPrenotazione.tariffe).toFixed(2)})
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Azioni */}
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setConfirmPrenotazione(null)}
                className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={() => {
                  const data = confirmPrenotazione;
                  setConfirmPrenotazione(null);
                  prenotaVoucherConQuantita(data.vouchers, data.quantita);
                }}
                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors"
              >
                ✓ Conferma Prenotazione
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Video Tutorial */}
      {showVideoModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Modale */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <PlayCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                    Video Tutorial
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    Scegli il video che desideri guardare
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowVideoModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Tab Selezione Video */}
            <div className="flex gap-2 px-4 sm:px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setSelectedVideo('rimborso')}
                className={`px-4 py-2 font-semibold text-sm rounded-t-lg transition-colors ${
                  selectedVideo === 'rimborso'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Come Funziona il Rimborso
              </button>
              <button
                onClick={() => setSelectedVideo('creazione')}
                className={`px-4 py-2 font-semibold text-sm rounded-t-lg transition-colors ${
                  selectedVideo === 'creazione'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Come Creare un Voucher
              </button>
            </div>

            {/* Video Container */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full border-0"
                src={
                  selectedVideo === 'rimborso'
                    ? 'https://www.youtube.com/embed/XGU7YUGjZHo?si=X4YJitDobsCmtjOP'
                    : 'https://www.youtube.com/embed/FrZuqZlriKM'
                }
                title={selectedVideo === 'rimborso' ? 'Video Tutorial Rimborso Voucher PagoPA' : 'Come Creare un Voucher dal Portale del Trasporto'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>

            {/* Footer Modale */}
            <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                {selectedVideo === 'rimborso' ? (
                  <>💡 <strong>Suggerimento:</strong> Guarda il video per scoprire tutte le funzionalità del servizio di rimborso voucher</>
                ) : (
                  <>💡 <strong>Suggerimento:</strong> Questo video ti mostra come creare un voucher dal portale del trasporto</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RimborsoVoucherPage;

